/* eslint-disable react/jsx-handler-names */
/* eslint-disable no-shadow */
import React, { useState, useEffect, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from '@tanstack/react-query';

import Button from '../../../../Button';
import SidePopup from '../../SidePopup';
import FormDropdown from '../../../../Form/FormDropdown';
import TextInput from '../../../../Form/TextInput';
import PhoneInput from '../../../../Form/PhoneInput';
import yupSchemaToFields from '../../../../../helpers/yupSchemaToFields';
import { UiContext } from '../../../../../context/UiContext';
import classes from './styles.module.scss';
import AdminService from '../../../../../services/AdminService';

export default function NewUserForm({
  handleClose,
  refetch,
  isVisible,
  setIsAddUserFormVisible,
}) {
  const [isTriedToSubmit, setIsTriedToSubmit] = useState(false);

  const { t } = useTranslation();

  const {
    showModal,
    showUnknownErrorModal,
    setIsFetching,
    setIsCreatingOrUpdating,
  } = useContext(UiContext);

  const createUserMutation = useMutation({
    mutationFn: ({
      firstName,
      middleName,
      phone,
      lastName,
      email,
      roles,
      designation,
    }) => {
      return AdminService.addUser({
        firstName,
        middleName,
        phone,
        lastName,
        email,
        roles,
        designation,
      });
    },
    onMutate: () => {
      setIsCreatingOrUpdating(true);
      setIsFetching(true);
    },
    onSuccess: () => {
      setIsCreatingOrUpdating(false);
      setIsFetching(false);
      refetch();
      showModal({
        title: t(
          'dashboardComponents.Console.UserManagement.NewUserForm.successModal.title'
        ),
        text: t(
          'dashboardComponents.Console.UserManagement.NewUserForm.successModal.text'
        ),
        onConfirm: () => {},
        onCancel: () => setIsAddUserFormVisible(true),
        dismissButtonLabel: t('common.addMore'),
        confirmButtonLabel: t('common.gotIt'),
      });
      handleClose();
    },
    onError: (error) => {
      setIsCreatingOrUpdating(false);
      setIsFetching(false);
      if (error.response.status === 409) {
        showModal({
          title: t(
            'dashboardComponents.Console.UserManagement.NewUserForm.duplicateUserModal.title'
          ),
          text: t(
            'dashboardComponents.Console.UserManagement.NewUserForm.duplicateUserModal.text'
          ),
          dismissButtonLabel: t(
            'dashboardComponents.Console.UserManagement.NewUserForm.duplicateUserModal.dismissButtonLabel'
          ),
        });
      } else {
        showUnknownErrorModal();
      }
    },
  });

  const validationSchema = useMemo(
    () =>
      yup.object({
        firstName: yup
          .string()
          .matches(/^[\p{L}\p{M}'-]+$/u, t('common.onlyOneWordAllowed'))
          .trim()
          .required(t('common.requiredField')),
        lastName: yup
          .string()
          .matches(/^[\p{L}\p{M}'-]+$/u, t('common.onlyOneWordAllowed'))
          .trim()
          .required(t('common.requiredField')),
        phone: yup
          .string()
          .trim()
          .required(t('common.cannotBeEmpty'))
          .test(
            'is-valid-phone',
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.phoneInvalid'
            ),
            async (value) => {
              if (!value) {
                return true; // Skip validation if the value is empty
              }

              const { isValidPhoneNumber, AsYouType } = await import(
                'libphonenumber-js'
              );

              const formattedNumber = new AsYouType().input(value);

              const formattedNumberWithPlus = formattedNumber.startsWith('+')
                ? formattedNumber
                : `+${formattedNumber}`;

              return isValidPhoneNumber(
                new AsYouType().input(formattedNumberWithPlus)
              );
            }
          ),
        middleName: yup
          .string()
          .trim()
          .nullable()
          .optional()
          .matches(/^[\p{L}\p{M}'-]*$/u, {
            message: t('common.onlyOneWordAllowed'),
            excludeEmptyString: true,
          }),
        email: yup
          .string()
          .trim()
          .email(
            t(
              'dashboardComponents.Console.UserManagement.NewUserForm.errorMessages.validEmail'
            )
          )
          .required(t('common.requiredField')),
        city: yup.array(),
        restaurant: yup.array(),
        designation: yup
          .string()
          .trim()
          .nullable()
          .optional()
          .max(
            100,
            t(
              'dashboardComponents.Console.UserManagement.NewUserForm.errorMessages.designationMaxLength'
            )
          ),
        role: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.UserManagement.NewUserForm.errorMessages.roleRequired'
            )
          ),
      }),
    [t]
  );

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      middleName: '',
      phone: '',
      designation: '',
      role: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const roles = [];
        if (values.role) {
          roles.push(values.role.value);
        }
        const requestData = {
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName,
          phone: values.phone,
          email: values.email,
          roles,
          designation: values.designation,
        };

        console.log('requestData', requestData);

        createUserMutation.mutate(requestData);
      } catch (error) {
        console.log(error);
      }
    },
  });

  const closeForm = () => {
    if (Object.values(formik.values).every?.((val) => !val)) {
      handleClose();
    } else {
      showModal({
        title: t(
          'dashboardComponents.Console.UserManagement.NewUserForm.exitFormConfirmation.title'
        ),
        text: t(
          'dashboardComponents.Console.UserManagement.NewUserForm.exitFormConfirmation.text'
        ),
        onConfirm: () => {},
        onCancel: handleClose,
        dismissButtonLabel: t('common.yes'),
        confirmButtonLabel: t('common.no'),
      });
    }
  };

  useEffect(() => {
    if (!isVisible) {
      formik.resetForm();
      setIsTriedToSubmit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  /* const isRightButtonDisabled =
    Object.values(formik.values).some((val) => !val) || isCreatingOrUpdating; */

  return (
    <SidePopup
      fields={yupSchemaToFields(validationSchema)}
      mode="add"
      formik={formik}
      isVisible={isVisible}
      handleClose={closeForm}
      title={t('dashboardComponents.Console.UserManagement.NewUserForm.title')}
      leftButtonLabel={t('common.clear')}
      onLeftButtonClick={formik.resetForm}
      rightButtonLabel={t('common.save')}
      rightButtonComponent={Button}
      onRightButtonClick={() => {
        setIsTriedToSubmit(true);
        formik.handleSubmit();
      }}
    >
      <div className={classes.NewUserForm}>
        <form onSubmit={formik.handleSubmit}>
          <div className={classes.leftCol}>
            <TextInput
              error={formik.errors.firstName}
              touched={isTriedToSubmit}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.labels.firstName'
              )}
              name="firstName"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.placeholders.firstName'
              )}
              height={50}
            />

            <TextInput
              error={formik.errors.lastName}
              touched={isTriedToSubmit}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.labels.lastName'
              )}
              name="lastName"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.placeholders.lastName'
              )}
              height={50}
            />
            <FormDropdown
              error={formik.errors.role}
              touched={isTriedToSubmit}
              value={formik.values.role}
              setFieldValue={formik.setFieldValue}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.labels.role'
              )}
              name="role"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.placeholders.role'
              )}
              height={50}
              fetchOptions={{
                dataName: 'rolesOptionsUser',
                getDataHandler: AdminService.getRolesUser,
              }}
              searchInfo={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.searchInfo.role'
              )}
              searchInfoWidth={173}
              setFieldTouched={formik.setFieldTouched}
            />
            <PhoneInput
              name="phone"
              label={t('intelCandidate.form.personal.phone')}
              placeholder="+1 555 555 5555"
              setFieldValue={formik.setFieldValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              touched={isTriedToSubmit}
              error={formik.errors.phone}
              value={formik.values.phone}
              height={45}
            />
          </div>
          <div className={classes.rightCol}>
            <TextInput
              error={formik.errors.middleName}
              touched={isTriedToSubmit}
              value={formik.values.middleName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.EditUser.labels.middleName'
              )}
              name="middleName"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.EditUser.placeholders.middleName'
              )}
              height={50}
            />
            <TextInput
              error={formik.errors.designation}
              touched={isTriedToSubmit}
              value={formik.values.designation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.AddUserForm.labels.designation'
              )}
              name="designation"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.AddUserForm.placeholders.designation'
              )}
              height={50}
              note={t('common.optional')}
            />
            <TextInput
              error={formik.errors.email}
              touched={isTriedToSubmit}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.labels.email'
              )}
              name="email"
              placeholder={t(
                'dashboardComponents.Console.UserManagement.NewUserForm.placeholders.email'
              )}
              height={50}
            />
          </div>
        </form>
      </div>
    </SidePopup>
  );
}
